import React, { useState, useEffect } from 'react';
import './DodajPracownika.css';
import rotationsAPI from '../../api/rotations';

function DodajPracownika({ users, projects, shifts, contracts, positions, onAddUser, onAddShift, onDeleteShift, onAddContract, onDeleteContract, onAddPosition, onDeletePosition, currentUser }) {
  const initialProject = projects.length > 0 ? projects[0] : '';
  const [selectedProject, setSelectedProject] = useState(initialProject);
  const [rotations, setRotations] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    position: '',
    shift: '',
    contract: '',
    employmentType: '',
    assignedUser: '',
    leader: '',
    project: initialProject
  });

  // Wczytaj rotacje dla wybranego projektu z API
  useEffect(() => {
    const loadRotations = async () => {
      if (!selectedProject) {
        setRotations([]);
        return;
      }
      
      try {
        const rotationsData = await rotationsAPI.getByProject(selectedProject);
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          project: r.project || selectedProject
        }));
        setRotations(formattedRotations);
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
        setRotations([]);
      }
    };
    
    loadRotations();
    
    // Nasłuchuj custom event dla zmian rotacji
    const handleRotationsUpdate = () => {
      loadRotations();
    };
    
    window.addEventListener('rotationsUpdated', handleRotationsUpdate);
    
    return () => {
      window.removeEventListener('rotationsUpdated', handleRotationsUpdate);
    };
  }, [selectedProject]);

  // Synchronizuj selectedProject z formData.project
  useEffect(() => {
    if (formData.project && formData.project !== selectedProject) {
      setSelectedProject(formData.project);
    }
  }, [formData.project, selectedProject]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showContractModal, setShowContractModal] = useState(false);
  const [newContractName, setNewContractName] = useState('');
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [newPositionName, setNewPositionName] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    
    if (name === 'project') {
      setSelectedProject(value);
      setFormData({
        ...formData,
        project: value,
        shift: '', // Resetuj rotację przy zmianie projektu
        contract: '', // Resetuj umowę przy zmianie projektu
        position: '' // Resetuj stanowisko przy zmianie projektu
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    setError('');
    setSuccess('');
  };

  // Pobierz dostępne rotacje dla wybranego projektu
  const getAvailableRotations = (project = null) => {
    const projectToUse = project || selectedProject;
    if (!projectToUse) return [];
    
    // Użyj rotacji załadowanych w useEffect
    const projectRotations = rotations.filter(r => r.project === projectToUse);
    return projectRotations.map(rotation => ({
      id: rotation.id,
      title: rotation.title,
      displayName: `${rotation.title} (Rota. ${rotation.rotationNumber})`
    }));
  };

  // Pobierz dostępne umowy dla wybranego projektu
  const getAvailableContracts = (project = null) => {
    const projectToUse = project || selectedProject;
    if (!projectToUse) return [];
    return contracts[projectToUse] || [];
  };

  // Pobierz dostępne stanowiska dla wybranego projektu
  const getAvailablePositions = (project = null) => {
    const projectToUse = project || selectedProject;
    if (!projectToUse) return [];
    return positions[projectToUse] || [];
  };

  // Pobierz listę liderów z użytkowników
  const getAvailableLeaders = () => {
    if (!users) return [];
    return users.filter(u => u.role === 'lider' && u.email && !u.email.includes('_'));
  };


  const isSuperAdmin = currentUser?.role === 'owner';
  const canManageShifts = isSuperAdmin || currentUser?.role === 'admin';
  const canManageContracts = isSuperAdmin || currentUser?.role === 'admin';
  const canManagePositions = isSuperAdmin || currentUser?.role === 'admin';

  // Usunięto handleAddNewShift - rotacje są zarządzane w sekcji Rotacje

  const handleAddNewContract = () => {
    setNewContractName('');
    setShowContractModal(true);
  };

  const handleContractModalConfirm = () => {
    if (!newContractName.trim() || !selectedProject) {
      setError('Proszę podać nazwę umowy');
      setShowContractModal(false);
      return;
    }

    const existingContracts = getAvailableContracts();
    if (existingContracts.includes(newContractName.trim())) {
      setError('Umowa o tej nazwie już istnieje dla tego klienta');
      setShowContractModal(false);
      return;
    }

    onAddContract(selectedProject, newContractName.trim());
    setFormData({ ...formData, contract: newContractName.trim() });
    setSuccess(`Umowa "${newContractName.trim()}" została dodana`);
    setShowContractModal(false);
    setNewContractName('');
  };

  const handleContractModalCancel = () => {
    setShowContractModal(false);
    setNewContractName('');
  };

  const handleAddNewPosition = () => {
    setNewPositionName('');
    setShowPositionModal(true);
  };

  const handlePositionModalConfirm = () => {
    if (!newPositionName.trim() || !selectedProject) {
      setError('Proszę podać nazwę stanowiska');
      setShowPositionModal(false);
      return;
    }

    const existingPositions = getAvailablePositions();
    if (existingPositions.includes(newPositionName.trim())) {
      setError('Stanowisko o tej nazwie już istnieje dla tego klienta');
      setShowPositionModal(false);
      return;
    }

    onAddPosition(selectedProject, newPositionName.trim());
    setFormData({ ...formData, position: newPositionName.trim() });
    setSuccess(`Stanowisko "${newPositionName.trim()}" zostało dodane`);
    setShowPositionModal(false);
    setNewPositionName('');
  };

  const handlePositionModalCancel = () => {
    setShowPositionModal(false);
    setNewPositionName('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.position || !formData.project || !formData.shift || !formData.contract || !formData.assignedUser) {
      setError('Proszę wypełnić wszystkie wymagane pola');
      return;
    }

    // Sprawdź czy projekt jest dostępny dla admina
    if (!projects.includes(formData.project)) {
      setError('Nie możesz przypisać pracownika do klienta, do którego nie jesteś przypisany');
      return;
    }

    // Sprawdź czy rotacja istnieje
    // Upewnij się, że używamy właściwego projektu (formData.project może być inny niż selectedProject)
    const projectForValidation = formData.project || selectedProject;
    
    // Wczytaj rotacje dla właściwego projektu z API
    let rotationsForProject = [];
    if (projectForValidation) {
      try {
        const rotationsData = await rotationsAPI.getByProject(projectForValidation);
        rotationsForProject = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          project: r.project || projectForValidation
        }));
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
      }
    }
    
    // Filtruj rotacje dla właściwego projektu
    const projectRotations = rotationsForProject.filter(r => r.project === projectForValidation);
    const availableRotations = projectRotations.map(rotation => ({
      id: rotation.id,
      title: rotation.title,
      displayName: `${rotation.title} (Rota. ${rotation.rotationNumber})`
    }));
    
    // Konwertuj formData.shift na string dla porównania
    const shiftId = formData.shift ? String(formData.shift) : null;
    const selectedRotation = availableRotations.find(r => {
      const rotationId = String(r.id);
      return rotationId === shiftId || r.title === formData.shift;
    });
    
    if (!selectedRotation && formData.shift) {
      setError('Wybrana rotacja nie istnieje dla tego klienta');
      return;
    }

    // Sprawdź czy umowa istnieje dla wybranego projektu
    const availableContracts = getAvailableContracts();
    if (!availableContracts.includes(formData.contract)) {
      setError('Wybrana umowa nie istnieje dla tego klienta');
      return;
    }

    // Sprawdź czy stanowisko istnieje dla wybranego projektu
    const availablePositions = getAvailablePositions();
    if (!availablePositions.includes(formData.position)) {
      setError('Wybrane stanowisko nie istnieje dla tego klienta');
      return;
    }

    // Sprawdź czy pracownik o tym samym imieniu, nazwisku i projekcie już istnieje
    const fullName = `${formData.firstName} ${formData.lastName}`;
    const existingEmployee = users.find(u => 
      u.firstName === formData.firstName && 
      u.lastName === formData.lastName && 
      u.projects && u.projects.includes(formData.project)
    );
    
    if (existingEmployee) {
      setError('Pracownik o tym imieniu i nazwisku jest już przypisany do tego klienta');
      return;
    }

    // Znajdź wybraną rotację - użyj rotationsForProject zamiast rotations
    const shiftIdForData = formData.shift ? String(formData.shift) : null;
    let selectedRotationData = rotationsForProject.find(r => String(r.id) === shiftIdForData);
    
    // Jeśli nie znaleziono, spróbuj znaleźć w availableRotations
    if (!selectedRotationData && selectedRotation) {
      selectedRotationData = rotationsForProject.find(r => String(r.id) === String(selectedRotation.id));
    }
    
    const rotationTitle = selectedRotationData ? selectedRotationData.title : (selectedRotation ? selectedRotation.title : formData.shift);

    try {
      await onAddUser({
        name: fullName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        position: formData.position,
        role: 'employee', // Pracownicy mają rolę 'employee' (nie logują się)
        projects: [formData.project], // Jeden projekt na pracownika
        shift: rotationTitle, // Zapisz tytuł rotacji jako "shift" dla kompatybilności
        rotationId: formData.shift, // Zapisz ID rotacji
        contract: formData.contract,
        employmentType: formData.employmentType || '', // Rodzaj zatrudnienia
        systemUser: formData.assignedUser, // Nazwa użytkownika w systemie zewnętrznym (np. SAP)
        leader: formData.leader || '', // Przypisany lider
        projectShift: { project: formData.project, shift: rotationTitle, rotationId: formData.shift }
      });

      setSuccess('Pracownik został dodany pomyślnie!');
      setFormData({
        firstName: '',
        lastName: '',
        position: '',
        shift: '',
        contract: '',
        employmentType: '',
        assignedUser: '',
        leader: '',
        project: projects.length > 0 ? projects[0] : ''
      });
      setSelectedProject(projects.length > 0 ? projects[0] : '');
    } catch (error) {
      console.error('Błąd dodawania pracownika:', error);
      setError(error.message || 'Nie udało się dodać pracownika. Sprawdź konsolę przeglądarki.');
    }
  };

  if (projects.length === 0) {
    return (
      <div className="dodaj-pracownika-container">
        <div className="dodaj-pracownika-section">
          <h3>Dodaj nowego pracownika</h3>
          <div className="admin-message error">
            Brak dostępnych klientów. Najpierw dodaj klienta w menu Admin.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dodaj-pracownika-container">
      <div className="dodaj-pracownika-section">
        <div className="form-header">
          <h3>Dodaj nowego pracownika</h3>
          <p className="form-subtitle">Wypełnij wszystkie wymagane pola, aby dodać nowego pracownika do systemu</p>
        </div>
        
        {error && <div className="admin-message error">{error}</div>}
        {success && <div className="admin-message success">{success}</div>}
        
        <form onSubmit={handleSubmit} className="add-employee-form">
          {/* Sekcja: Dane osobowe */}
          <div className="form-section">
            <div className="form-section-header">
              <h4 className="form-section-title">Dane osobowe</h4>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">
                  Imię <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Jan"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">
                  Nazwisko <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Kowalski"
                  required
                />
              </div>
            </div>
          </div>

          {projects.length > 0 && (
            <>
              {/* Sekcja: Informacje o pracy */}
              <div className="form-section">
                <div className="form-section-header">
                  <h4 className="form-section-title">Informacje o pracy</h4>
                </div>
                <div className="form-row form-row-three">
                  <div className="form-group">
                    <label htmlFor="project">
                      Klient <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="project"
                      name="project"
                      value={formData.project}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Wybierz klienta</option>
                      {projects.map((project, idx) => (
                        <option key={idx} value={project}>{project}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="position">
                      <span>Stanowisko <span className="required-asterisk">*</span></span>
                      {canManagePositions && selectedProject && (
                        <button
                          type="button"
                          onClick={handleAddNewPosition}
                          className="add-item-button add-position-button"
                          title="Dodaj nowe stanowisko dla tego klienta"
                        >
                          <span className="button-icon">+</span>
                          <span className="button-text">Dodaj</span>
                        </button>
                      )}
                    </label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      disabled={!selectedProject}
                    >
                      <option value="">{selectedProject ? 'Wybierz stanowisko' : 'Najpierw wybierz klienta'}</option>
                      {getAvailablePositions().map((position, idx) => (
                        <option key={idx} value={position}>{position}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="shift">
                      Rotacja <span className="required-asterisk">*</span>
                    </label>
                    <select
                      id="shift"
                      name="shift"
                      value={formData.shift}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Wybierz rotację</option>
                      {getAvailableRotations().map((rotation) => (
                        <option key={rotation.id} value={rotation.id}>{rotation.displayName}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="leader">
                      Przypisz Lidera
                    </label>
                    <select
                      id="leader"
                      name="leader"
                      value={formData.leader}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz lidera (opcjonalnie)</option>
                      {getAvailableLeaders().map((leader) => (
                        <option key={leader.email} value={leader.email}>{leader.name} ({leader.email})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Sekcja: Informacje o umowie */}
              <div className="form-section">
                <div className="form-section-header">
                  <h4 className="form-section-title">Informacje o umowie</h4>
                </div>
                <div className="form-row form-row-three">
                  <div className="form-group">
                    <label htmlFor="contract">
                      <span>Typ umowy <span className="required-asterisk">*</span></span>
                      {canManageContracts && selectedProject && (
                        <button
                          type="button"
                          onClick={handleAddNewContract}
                          className="add-item-button add-position-button"
                          title="Dodaj nową umowę dla tego klienta"
                        >
                          <span className="button-icon">+</span>
                          <span className="button-text">Dodaj</span>
                        </button>
                      )}
                    </label>
                    <select
                      id="contract"
                      name="contract"
                      value={formData.contract}
                      onChange={handleChange}
                      required
                      disabled={!selectedProject}
                    >
                      <option value="">{selectedProject ? 'Wybierz umowę' : 'Najpierw wybierz klienta'}</option>
                      {getAvailableContracts().map((contract, idx) => (
                        <option key={idx} value={contract}>{contract}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="employmentType">
                      Rodzaj zatrudnienia
                    </label>
                    <select
                      id="employmentType"
                      name="employmentType"
                      value={formData.employmentType}
                      onChange={handleChange}
                    >
                      <option value="">Wybierz rodzaj zatrudnienia</option>
                      <option value="Umowa o pracę">Umowa o pracę</option>
                      <option value="Umowa zlecenie">Umowa zlecenie</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="assignedUser">
                      Użytkownik systemu (np. SAP) <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="assignedUser"
                      name="assignedUser"
                      value={formData.assignedUser}
                      onChange={handleChange}
                      placeholder="np. JAN.KOWALSKI, USER001"
                      required
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          
          <div className="form-submit-wrapper">
            <button type="submit" className="add-employee-button">
              <span className="button-icon">✓</span>
              <span>Dodaj pracownika</span>
            </button>
          </div>
        </form>

        {/* Modal dla dodawania nowej umowy */}
        {showContractModal && (
          <div className="modal-overlay" onClick={handleContractModalCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Dodaj nową umowę</h3>
              <div className="modal-form-group">
                <label htmlFor="new-contract-name">Nazwa umowy:</label>
                <input
                  type="text"
                  id="new-contract-name"
                  value={newContractName}
                  onChange={(e) => setNewContractName(e.target.value)}
                  placeholder="np. Umowa o pracę"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleContractModalConfirm();
                    } else if (e.key === 'Escape') {
                      handleContractModalCancel();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={handleContractModalCancel}
                >
                  Anuluj
                </button>
                <button 
                  className="btn-confirm"
                  onClick={handleContractModalConfirm}
                >
                  Dodaj
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal dla dodawania nowego stanowiska */}
        {showPositionModal && (
          <div className="modal-overlay" onClick={handlePositionModalCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Dodaj nowe stanowisko</h3>
              <div className="modal-form-group">
                <label htmlFor="new-position-name">Nazwa stanowiska:</label>
                <input
                  type="text"
                  id="new-position-name"
                  value={newPositionName}
                  onChange={(e) => setNewPositionName(e.target.value)}
                  placeholder="np. Operator, Technolog"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handlePositionModalConfirm();
                    } else if (e.key === 'Escape') {
                      handlePositionModalCancel();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={handlePositionModalCancel}
                >
                  Anuluj
                </button>
                <button 
                  className="btn-confirm"
                  onClick={handlePositionModalConfirm}
                >
                  Dodaj
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DodajPracownika;

